import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { AuthContextValue, Profile } from '@/types';
import { AuthContext } from './AuthContext';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Provides auth state to the entire component tree.
 *
 * Responsibilities:
 * - Hydrates session on mount via supabase.auth.getSession()
 * - Subscribes to auth state changes (sign-in, sign-out, token refresh)
 * - Loads the profiles row for the authenticated user
 * - Handles profile-missing case (signs out + shows error)
 * - Notifies the user on session expiry
 * - Exposes: user, session, profile, clientId, role, isLoading, signOut
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Track whether the initial hydration completed so we can distinguish
  // an expiry event from the initial unauthenticated state.
  const hydratedRef = useRef(false);

  const loadProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, client_id, role, full_name, email, created_at, updated_at')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      // Dev visibility only — replace with structured logger in production.
      console.error('[AuthProvider] profile query error', { message: error.message });
      return null;
    }

    return data as Profile | null;
  }, []);

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  }, []);

  useEffect(() => {
    // ── Initial hydration ───────────────────────────────────────────────────
    void supabase.auth.getSession().then(async ({ data }) => {
      const activeSession = data.session;
      setSession(activeSession);
      setUser(activeSession?.user ?? null);

      if (activeSession?.user) {
        const loaded = await loadProfile(activeSession.user.id);

        if (!loaded) {
          // Auth succeeded but profile is missing — sign out immediately.
          toast.error('Your account is not configured. Contact your administrator.');
          await supabase.auth.signOut();
          setUser(null);
          setSession(null);
        }

        setProfile(loaded);
      }

      setIsLoading(false);
      hydratedRef.current = true;
    });

    // ── Auth state change listener ──────────────────────────────────────────
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        const wasAuthenticated = session !== null;

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (!newSession) {
          setProfile(null);

          // Only show expiry toast after the initial hydration is done and
          // the user was previously signed in (genuine expiry, not initial load).
          if (hydratedRef.current && wasAuthenticated) {
            toast.error('Your session has expired. Please sign in again.');
          }
          return;
        }

        void loadProfile(newSession.user.id).then(async (loaded) => {
          if (!loaded) {
            toast.error('Your account is not configured. Contact your administrator.');
            await supabase.auth.signOut();
            setUser(null);
            setSession(null);
            setProfile(null);
            return;
          }
          setProfile(loaded);
        });
      },
    );

    return () => {
      listener.subscription.unsubscribe();
    };
    // loadProfile and handleSignOut are stable (useCallback with no deps).
    // session intentionally NOT in deps — wasAuthenticated check uses captured ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProfile]);

  const value: AuthContextValue = {
    user,
    session,
    profile,
    clientId: profile?.client_id ?? null,
    role: profile?.role ?? null,
    // Still loading if: hydration in progress OR session exists but profile not yet fetched.
    isLoading: isLoading || (session !== null && profile === null && hydratedRef.current),
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
